import React, { useEffect } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes, Content } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Plugin } from '@tiptap/pm/state';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ALLOWED_VARIABLES = [
    "{{CONTRACT_NO}}",
    "{{SETTLEMENT_NO}}",
    "{{CUSTOMER_NAME}}",
    "{{UOM_AMOUNT}}",
    "{{UOM}}",
    "{{AMOUNT}}",
];

interface VariableOptions {
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        variable: {
            insertVariable: (attributes: { variableName: string }) => ReturnType;
        };
    }
}

const VariableNode = Node.create<VariableOptions>({
    name: 'variablePlaceholder',
    group: 'inline',
    inline: true,
    atom: true,

    addOptions() {
        return {
            HTMLAttributes: {
                class: 'inline-block bg-blue-100 text-blue-800 border border-blue-300 rounded px-2 py-0.5 mx-1 font-mono text-sm cursor-default',
            },
        };
    },

    addAttributes() {
        return {
            variableName: {
                default: null,
                parseHTML: element => element.getAttribute('data-variable-name'),
                renderHTML: attributes => {
                    if (!attributes.variableName) {
                        return {};
                    }
                    return {
                        'data-variable-name': attributes.variableName,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-variable-name]',
            },
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        return [
            'span',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            `{{${node.attrs.variableName}}}`,
        ];
    },

    renderText({ node }) {
        return `{{${node.attrs.variableName}}}`;
    },

    addCommands() {
        return {
            insertVariable: attributes => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: attributes,
                });
            },
        };
    },
});

// --- Helper Function to Parse String to Tiptap JSON ---

const parseValueToTiptapJson = (textValue: string): Content => {
    const lines = textValue.split('\n');
    const docContent: any[] = [];
    // Regex to find variables like {{VAR_NAME}}
    // It ensures variables are surrounded by {{ and }} and captures the content.
    const variableRegex = /\{\{([A-Z_]+)\}\}/g;

    lines.forEach(line => {
        const paragraphContent: any[] = [];
        let lastIndex = 0;
        let match;

        // Find all variable matches in the current line
        while ((match = variableRegex.exec(line)) !== null) {
            // Add text segment before the found variable
            if (match.index > lastIndex) {
                paragraphContent.push({
                    type: 'text',
                    text: line.substring(lastIndex, match.index),
                });
            }
            // Add the variable placeholder node
            paragraphContent.push({
                type: 'variablePlaceholder',
                attrs: { variableName: match[1] }, // match[1] is the captured group (the variable name)
            });
            lastIndex = variableRegex.lastIndex; // Update the index for the next search
        }

        // Add any remaining text segment after the last variable
        if (lastIndex < line.length) {
            paragraphContent.push({
                type: 'text',
                text: line.substring(lastIndex),
            });
        }

        // Create the paragraph node. Add content only if it exists.
        docContent.push({
            type: 'paragraph',
            // Tiptap expects undefined for empty content, not an empty array
            content: paragraphContent.length > 0 ? paragraphContent : undefined,
        });
    });

    // Return the final document structure
    return {
        type: 'doc',
        content: docContent,
    };
};

interface DeferLanguageEditorProps {
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    editable?: boolean;
    className?: string;
}

export function DeferLanguageEditor({ value, onChange, disabled = false, editable, className }: DeferLanguageEditorProps) {
    const editor = useEditor({
        extensions: [
            // NOTE: prevent rich styling other then the custom variable node
            // NOTE: we store this as plain text not editor content
            StarterKit.configure({
                heading: false,
                bold: false,
                italic: false,
                strike: false,
                bulletList: false,
                orderedList: false,
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
            }),
            VariableNode,
        ],
        content: '', // Initialize empty, will be set by useEffect
        editable: !disabled || editable,
        onUpdate: ({ editor }) => {
            onChange(editor.getText({ blockSeparator: '\n' }));
        },
        editorProps: {
            attributes: {
                class: cn(
                    'prose dark:prose-invert focus:outline-none min-h-[150px] w-full border border-input rounded-md p-2',
                    disabled ? 'bg-muted/50 cursor-not-allowed' : 'bg-background',
                    className
                ),
            },
        },
    });

    // Effect to set initial content correctly parsing newlines and variables
    useEffect(() => {
        // Set initial content when the editor is ready and the value is provided
        if (editor && !editor.isDestroyed && value && editor.isEmpty) {
            const jsonContent = parseValueToTiptapJson(value);
            editor.commands.setContent(jsonContent, false); // Don't emit update
        }
    }, [editor, value]); // Depend on value as well

    // Effect to handle subsequent updates to the value prop
    useEffect(() => {
        // Run only if the editor is initialized, not empty, and the prop value has changed
        if (editor && !editor.isDestroyed && !editor.isEmpty) {
            const editorText = editor.getText({ blockSeparator: '\n' });
            // Only update if the external value differs from the editor's text representation
            if (value !== editorText) {
                const { from, to } = editor.state.selection; // Store selection
                const jsonContent = parseValueToTiptapJson(value);
                editor.commands.setContent(jsonContent, false); // Set new content, don't emit update
                // Try to restore previous selection
                editor.commands.setTextSelection({ from: Math.min(from, editor.state.doc.content.size), to: Math.min(to, editor.state.doc.content.size) });
            }
        }
    }, [value, editor]); // Re-run if value or editor instance changes

    const handleInsertVariable = (variable: string) => {
        const variableName = variable.slice(2, -2); // Extract name from {{NAME}}
        editor?.chain().focus().insertVariable({ variableName }).run();
    };

    if (!editor) {
        return null;
    }

    return (
        <div className="w-full">
            {!disabled && (
                <div className="border border-input bg-transparent rounded-md p-1 mb-2 flex flex-wrap gap-1">
                    <span className="text-sm font-medium p-1 mr-2 self-center">Insert Variable:</span>
                    {ALLOWED_VARIABLES.map(variable => (
                        <Button
                            key={variable}
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleInsertVariable(variable)}
                            className="font-mono text-xs h-7"
                        >
                            {variable}
                        </Button>
                    ))}</div>
            )}
            <EditorContent editor={editor} />
        </div>
    );
}

export default DeferLanguageEditor;